/**
 * The geometry both token lists are built to — the Dashboard's Popular
 * tokens block and the Tokens screen — taken from the design (Figma
 * "Home 3", a 402-wide frame): a 129 price column right-aligned, an
 * 80 × 32.5 chart, and 36 either side of the price.
 *
 * Those figures fit the frame they were drawn in. On a 360dp Android
 * phone the row has 312 to work with and the fixed parts come to 313:
 * the name column was handed −1 and collapsed, so a list of tokens read
 * "A…", "B…", "C…". The Dashboard block had the same arithmetic and the
 * same fate, one screen over.
 *
 * So the figures are maximums, not constants. The name keeps a floor and
 * never gives way; the gaps and the chart shrink into the difference. On
 * anything as wide as the design nothing shrinks at all and the rows are
 * the design to the point.
 *
 * React Native's flexShrink defaults to 0, unlike the web's 1 — only what
 * is marked below gives ground.
 */
export const CHART_WIDTH = 80;
export const CHART_HEIGHT = 32.5;
export const PRICE_WIDTH = 129;
export const COLUMN_GAP = 36;

/**
 * The floor under the name column: the width the design itself gives it
 * on the Dashboard, rounded down. Set any higher and a phone the size of
 * the design would start shrinking the gaps, which is the one case where
 * nothing should move. Set lower and a ticker starts to clip.
 */
export const NAME_MIN_WIDTH = 52;

/**
 * The icon and the space after it — the column the name starts behind.
 * The list rows leave 10 after the icon, the Dashboard's cards 8.
 *
 * A header above either list has to reserve the same width, empty. While
 * the name column still has slack it absorbs the difference and the
 * columns line up anyway, which is why the omission went unseen; once the
 * name is down to its floor the header and the row divide a different
 * remainder between the gaps and the chart, and the price column drifts —
 * 24 points apart on a 360dp phone, 8 on the 402 the design was drawn at.
 */
export const ICON_SIZE = 22;
export const LIST_ICON_COLUMN = ICON_SIZE + 10;
export const CARD_ICON_COLUMN = ICON_SIZE + 8;

export interface TokenRowWidths {
  name: number;
  gap: number;
  chart: number;
  price: number;
}

/**
 * What the parts come to inside a row `content` points wide, `icon` being
 * the icon and the space after it.
 *
 * This is flexbox worked out by hand, so a test can say what a 360dp
 * phone gets without rendering anything: the name grows into free space
 * and stops at its floor; past that the gaps and the chart shrink in
 * proportion to their own widths, which is what flexShrink does.
 */
export const tokenRowWidths = (content: number, icon: number): TokenRowWidths => {
  const fixed = icon + PRICE_WIDTH;
  const shrinkable = COLUMN_GAP * 2 + CHART_WIDTH;
  const free = content - fixed - shrinkable;

  if (free >= NAME_MIN_WIDTH) {
    return { name: free, gap: COLUMN_GAP, chart: CHART_WIDTH, price: PRICE_WIDTH };
  }

  const over = NAME_MIN_WIDTH - free;
  const scale = Math.max(0, 1 - over / shrinkable);
  return {
    name: NAME_MIN_WIDTH,
    gap: COLUMN_GAP * scale,
    chart: CHART_WIDTH * scale,
    price: PRICE_WIDTH,
  };
};
